import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from "../lib/api";
import { CheckCircle, Star, Truck } from 'lucide-react';
import FeatureHeader from '../components/FeatureHeader';

const styles = {
  page: {
    minHeight: '100vh',
    background: 'var(--bg)',
    fontFamily: "'Poppins', sans-serif",
    paddingBottom: 100,
  },
  contentWrap: {
    maxWidth: 640,
    margin: '0 auto',
    padding: '0 16px',
    boxSizing: 'border-box',
  },
  header: {
    background: 'var(--bg)',
    color: 'var(--on-bg)',
    position: 'relative',
    width: '100%',
    overflow: 'visible',
    borderBottomLeftRadius: 25,
    borderBottomRightRadius: 25,
    marginBottom: 20,
    padding: '18px 0 8px',
    fontFamily: "'Poppins', sans-serif",
  },
  headerTop: {
    display: 'flex',
    marginTop: 0,
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  backArrow: {
    cursor: 'pointer',
    flexShrink: 0,
    fontSize: 22,
    fontWeight: 700,
    color: 'var(--primary)',
    marginTop: 4,
  },
  headerTitle: {
    margin: 0,
    fontWeight: 700,
    fontSize: 22,
    marginLeft: 12,
    color: 'var(--on-bg)',
    letterSpacing: '0.3px',
    textAlign: 'center',
    lineHeight: 1.15,
  },
  headerSubtitle: {
    fontSize: 12,
    marginTop: 4,
    marginLeft: 34,
    textAlign: 'left',
  },
  trustStrip: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 6,
    marginBottom: 10,
    marginTop: 8,
    background: '#ffffff',
    border: '1px solid #eceef1',
    borderRadius: 999,
    padding: '6px 8px',
    overflowX: 'auto',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
  },
  trustItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    minWidth: 142,
  },
  trustIcon: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    border: '1px solid #eceef1',
    background: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  trustTitle: {
    fontSize: 11,
    color: '#3D3A52',
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  trustSubtitle: {
    fontSize: 9,
    color: '#A0A0B2',
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
  trustDivider: {
    width: 1,
    height: 20,
    background: '#d6d2e7',
    flexShrink: 0,
  },
  searchInput: {
    width: '100%',
    padding: '12px 14px',
    marginBottom: '20px',
    fontSize: '15px',
    borderRadius: '12px',
    border: '1px solid #e3e3e8',
    background: '#fff',
    color: 'var(--on-bg)',
    outline: 'none',
    boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
  },
  grid: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: 16,
  },
  card: {
    borderRadius: 16,
    backgroundColor: 'var(--surface)',
    overflow: 'hidden',
    boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.14s ease, box-shadow 0.2s ease',
  },
  cardImageWrap: {
    width: '100%',
    height: 150,
    overflow: 'hidden',
    position: 'relative',
  },
  cardImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  verifiedBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    background: 'var(--primary)',
    color: '#fff',
    borderRadius: '50%',
    padding: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardBody: {
    padding: '10px 12px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  cardName: {
    fontSize: 14,
    fontWeight: 600,
    margin: 0,
    color: 'var(--on-bg)',
    lineHeight: 1.35,
  },
  cardMeta: {
    margin: 0,
    fontSize: 12,
    color: '#6B7280',
    lineHeight: 1.35,
  },
  ratingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    color: 'var(--on-bg)',
    fontSize: 12,
  },
  deliveryBadge: {
    marginTop: 2,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 11,
    fontWeight: 500,
    color: 'var(--primary)',
  },
  emptyState: {
    textAlign: 'center',
    color: '#8b8f98',
    marginTop: 10,
  },
};

const PharmacySearchPage = () => {
  const [pharmacies, setPharmacies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPharmacies, setFilteredPharmacies] = useState([]);
  const [isTrustStripPaused, setIsTrustStripPaused] = useState(false);
  const trustStripRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/api/pharmacy")
      .then(response => {
        setPharmacies(response.data);
        setFilteredPharmacies(response.data);
      })
      .catch(error => {
        console.error('Error fetching pharmacies:', error);
      });
  }, []);

  useEffect(() => {
    const filtered = pharmacies.filter(pharmacy =>
      pharmacy.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredPharmacies(filtered);
  }, [searchTerm, pharmacies]);

  useEffect(() => {
    const strip = trustStripRef.current;
    if (!strip || isTrustStripPaused) return undefined;

    const interval = setInterval(() => {
      const resetPoint = strip.scrollWidth / 2;
      if (resetPoint <= 0) return;
      if (strip.scrollLeft >= resetPoint) {
        strip.scrollLeft -= resetPoint;
      }
      strip.scrollBy({ left: 1, behavior: 'auto' });
    }, 24);

    return () => clearInterval(interval);
  }, [isTrustStripPaused]);

  const handlePharmacyClick = (id) => {
    navigate(`/pharmacy/${id}`);
  };

  const trustHighlights = [
    { icon: <CheckCircle size={15} color="#a78bfa" />, title: 'Verified Stores', subtitle: 'Trusted pharmacy partners' },
    { icon: <Star size={15} color="#a78bfa" />, title: 'Top Rated', subtitle: 'Community-approved care' },
    { icon: <Truck size={15} color="#a78bfa" />, title: 'Fast Delivery', subtitle: 'Convenient doorstep options' },
  ];

  return (
    <div style={styles.page}>
      <div style={styles.contentWrap}>
        <div style={styles.header}>
          <FeatureHeader
            title="Pharmacies"
            subtitle="Find trusted pharmacies near you"
            onBack={() => navigate(-1)}
            containerStyle={{ padding: 0 }}
          />

          
        </div>

        <input
          type="text"
          placeholder="Search pharmacies..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={styles.searchInput}
        />
        <ul style={styles.grid}>
          {filteredPharmacies.map(pharmacy => (
          <li
            key={pharmacy._id}
            onClick={() => handlePharmacyClick(pharmacy._id)}
            style={styles.card}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 10px 22px rgba(0,0,0,0.12)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.08)';
            }}
          >
            <div style={styles.cardImageWrap}>
              <img
                src={pharmacy.logo || 'https://via.placeholder.com/300x150'}
                alt={pharmacy.name}
                style={styles.cardImage}
              />
              {pharmacy.verified && (
                <div style={styles.verifiedBadge}>
                  <CheckCircle size={12} />
                </div>
              )}
            </div>
            <div style={styles.cardBody}>
              <h3 style={styles.cardName}>{pharmacy.name}</h3>
              <p style={styles.cardMeta}>{pharmacy.address}, {pharmacy.city}</p>
              <div style={styles.ratingRow}>
                <Star size={12} fill="#FFD700" color="#FFD700" />
                <span>{pharmacy.rating || '4.5'}</span>
                <span>({pharmacy.reviews || '100'})</span>
              </div>
              {pharmacy.deliveryAvailable && (
                <div style={styles.deliveryBadge}>
                  <Truck size={10} />
                  <span>Free Delivery</span>
                </div>
              )}
            </div>
          </li>
          ))}
        </ul>
        {filteredPharmacies.length === 0 && (
          <p style={styles.emptyState}>No pharmacies found.</p>
        )}
      </div>
    </div>
  );
};

export default PharmacySearchPage;
